/**
 * ErrorBoundary - keeps one broken screen from blanking the whole app.
 *
 * Mirrors BEETS, which wraps each route in one. A white page tells nobody
 * anything; this at least names the screen that failed and offers a way back.
 */

import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'

interface Props {
  children: ReactNode
  title?: string
}

interface State {
  error: Error | null
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Nothing collects front-end errors yet, so the console is where a
    // developer will actually look for the stack.
    console.error('BEST: unhandled error in a screen', error, info.componentStack)
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => this.setState({ error: null })}>
              Try again
            </Button>
          }
        >
          <AlertTitle>{this.props.title ?? 'Something went wrong'}</AlertTitle>
          {error.message}
        </Alert>
      </Box>
    )
  }
}
